// shop-frontend/src/pages/ShopReviewsPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Star,
  MessageSquare,
  Send,
  ChevronDown,
  RefreshCw,
  Filter,
  X,
  ArrowLeft,
  Package,
} from "lucide-react";
import { getShopReviews, respondToReview, deleteReviewResponse, toAbsUrl } from "../utils/api";
import "../styles/shopReviews.css";

/* Image with a fallback block (the app has no placeholder image files) */
function Thumb({ src, alt, round, fallback }) {
  const [broken, setBroken] = useState(false);
  const url = src ? toAbsUrl(src) : "";
  const cls = `w-10 h-10 ${round ? "rounded-full" : "rounded"} flex-shrink-0`;
  if (!url || broken) {
    return (
      <div className={`${cls} bg-zinc-700 flex items-center justify-center text-zinc-200 font-semibold`}>
        {fallback}
      </div>
    );
  }
  return <img src={url} alt={alt || ""} className={`${cls} object-cover bg-zinc-800`} onError={() => setBroken(true)} />;
}

/* =========================
   Star Rating Display
   ========================= */
function StarRating({ rating, size = "sm" }) {
  const sizes = { sm: "w-3 h-3", md: "w-4 h-4", lg: "w-5 h-5" };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${sizes[size]} ${
            i <= rating ? "text-yellow-400 fill-yellow-400" : "text-zinc-600"
          }`}
        />
      ))}
    </div>
  );
}

/* =========================
   Rating Distribution Bar
   ========================= */
function RatingBar({ rating, count, total }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-zinc-400 w-4">{rating}</span>
      <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
      <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-yellow-400 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm text-zinc-500 w-8">{count}</span>
    </div>
  );
}

/* =========================
   Response Form
   ========================= */
function ResponseForm({ review, onSubmit, onCancel }) {
  const [text, setText] = useState(review.shopResponse?.text || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    
    setLoading(true);
    setError("");
    try {
      await onSubmit(review._id, text.trim());
    } catch (err) {
      setError(err?.message || "Could not send your response. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-3 p-3 bg-zinc-800 rounded-lg">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write your response to this review..."
        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500 resize-none"
        rows={3}
        maxLength={1000}
      />
      {error && <div className="text-xs text-red-400 mt-1">{error}</div>}
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-zinc-500">{text.length}/1000</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !text.trim()}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Send className="w-3 h-3" />
            {loading ? "Sending..." : review.shopResponse?.text ? "Update" : "Send"}
          </button>
        </div>
      </div>
    </form>
  );
}

/* =========================
   Review Card
   ========================= */
function ReviewCard({ review, onRespond, onDeleteResponse }) {
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleRespond = async (reviewId, text) => {
    await onRespond(reviewId, text);
    setShowForm(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete your response? The customer will no longer see it.")) return;
    try {
      await onDeleteResponse(review._id);
    } catch (err) {
      window.alert(err?.message || "Could not delete the response.");
    }
  };

  const productId = typeof review.productId === "object" ? review.productId?._id : review.productId;
  const productTitle = review.productTitle || review.productId?.title || "Product";
  const name = review.userName || "Customer";

  const commentTruncated = review.comment?.length > 200 && !expanded;
  const displayComment = commentTruncated
    ? review.comment.slice(0, 200) + "..."
    : review.comment;

  return (
    <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Thumb src={review.avatarUrl} alt={name} round fallback={name.trim().charAt(0).toUpperCase() || "?"} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium">{name}</span>
            <StarRating rating={review.rating} />
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {new Date(review.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Product Info */}
      {productId && (
        <Link
          to={`/shop/product/${encodeURIComponent(productId)}`}
          className="flex items-center gap-2 mt-3 p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
        >
          <Thumb src={review.productImage} alt={productTitle} fallback={<Package className="w-5 h-5" />} />
          <span className="text-sm text-zinc-300 truncate">{productTitle}</span>
        </Link>
      )}

      {/* Comment */}
      {review.comment && (
        <div className="mt-3">
          <p className="text-zinc-300 text-sm whitespace-pre-wrap">{displayComment}</p>
          {review.comment.length > 200 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-emerald-400 hover:text-emerald-300 mt-1"
            >
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>
      )}

      {/* Media */}
      {review.media?.length > 0 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {review.media.map((m, i) => (
            <img
              key={i}
              src={toAbsUrl(m.url)}
              alt={`Review media ${i + 1}`}
              className="w-20 h-20 rounded-lg object-cover bg-zinc-800 flex-shrink-0"
            />
          ))}
        </div>
      )}

      {/* Shop Response */}
      {review.shopResponse?.text && !showForm && (
        <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-emerald-400 font-medium">Your Response</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowForm(true)}
                className="text-xs text-zinc-400 hover:text-white"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Delete
              </button>
            </div>
          </div>
          <p className="text-sm text-zinc-300 whitespace-pre-wrap">
            {review.shopResponse.text}
          </p>
          {review.shopResponse.respondedAt && (
            <div className="text-xs text-zinc-500 mt-1">
              {new Date(review.shopResponse.respondedAt).toLocaleDateString()}
            </div>
          )}
        </div>
      )}

      {/* Response Form Toggle */}
      {!review.shopResponse?.text && !showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 mt-3 text-sm text-emerald-400 hover:text-emerald-300"
        >
          <MessageSquare className="w-4 h-4" />
          Respond to this review
        </button>
      )}

      {/* Response Form */}
      {showForm && (
        <ResponseForm
          review={review}
          onSubmit={handleRespond}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

/* =========================
   Main Reviews Page
   ========================= */
export default function ShopReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [filterOpen, setFilterOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const buildParams = useCallback((p) => {
    const params = { sort, page: p };
    if (filter !== "all") params.filter = filter;
    if (ratingFilter) params.rating = ratingFilter;
    return params;
  }, [filter, ratingFilter, sort]);

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const data = await getShopReviews(buildParams(1));
      setReviews(data.reviews || []);
      setStats(data.stats || null);
      setPage(1);
      setPages(Math.max(1, Number(data.pages || 1)));
    } catch (err) {
      console.error("Failed to load reviews:", err);
      setLoadError(err?.message || "Could not load reviews.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = async () => {
    const next = page + 1;
    setLoadingMore(true);
    try {
      const data = await getShopReviews(buildParams(next));
      setReviews((prev) => {
        const seen = new Set(prev.map((r) => String(r._id)));
        return [...prev, ...(data.reviews || []).filter((r) => !seen.has(String(r._id)))];
      });
      setPage(next);
      setPages(Math.max(1, Number(data.pages || 1)));
    } catch (err) {
      setLoadError(err?.message || "Could not load more reviews.");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  // Update the one review in place, so the pages already loaded stay put.
  const handleRespond = async (reviewId, text) => {
    await respondToReview(reviewId, text);
    setReviews((prev) => prev.map((r) => (String(r._id) === String(reviewId)
      ? { ...r, shopResponse: { ...(r.shopResponse || {}), text, respondedAt: new Date().toISOString() } }
      : r)));
  };

  const handleDeleteResponse = async (reviewId) => {
    await deleteReviewResponse(reviewId);
    setReviews((prev) => prev.map((r) => (String(r._id) === String(reviewId) ? { ...r, shopResponse: null } : r)));
  };

  return (
    <div className="min-h-screen bg-zinc-950 page-responsive shop-reviews">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/shop/dashboard"
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">Customer Reviews</h1>
            <p className="text-zinc-400 text-sm">
              Manage and respond to customer feedback
            </p>
          </div>
          <button
            onClick={loadReviews}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
              <div className="text-3xl font-bold text-white">{stats.total}</div>
              <div className="text-sm text-zinc-400">Total Reviews</div>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
              <div className="flex items-center justify-center gap-1">
                <span className="text-3xl font-bold text-yellow-400">{stats.avgRating}</span>
                <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
              </div>
              <div className="text-sm text-zinc-400">Average Rating</div>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
              <div className="text-3xl font-bold text-orange-400">{stats.pending}</div>
              <div className="text-sm text-zinc-400">Awaiting Response</div>
            </div>
            <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 text-center">
              <div className="text-3xl font-bold text-emerald-400">{stats.responded}</div>
              <div className="text-sm text-zinc-400">Responded</div>
            </div>
          </div>
        )}

        {/* Rating Distribution */}
        {stats?.ratingDistribution && (
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800 mb-6">
            <h3 className="text-white font-medium mb-3">Rating Distribution</h3>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <RatingBar
                  key={rating}
                  rating={rating}
                  count={stats.ratingDistribution[rating] || 0}
                  total={stats.total}
                />
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="relative">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">
                {filter === "all" ? "All Reviews" : filter === "pending" ? "Need Response" : "Responded"}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            {filterOpen && (
              <div className="absolute left-0 mt-2 w-40 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20">
                {[
                  { key: "all", label: "All Reviews" },
                  { key: "pending", label: "Need Response" },
                  { key: "responded", label: "Responded" },
                ].map((f) => (
                  <button
                    key={f.key}
                    onClick={() => {
                      setFilter(f.key);
                      setFilterOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-zinc-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      filter === f.key ? "text-emerald-400" : "text-white"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rating Filter */}
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
          >
            <option value="">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>

          {/* Sort */}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>

          {/* Clear filters */}
          {(filter !== "all" || ratingFilter || sort !== "newest") && (
            <button
              onClick={() => {
                setFilter("all");
                setRatingFilter("");
                setSort("newest");
              }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        {loadError && (
          <div className="mb-4 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-sm text-red-300">
            {loadError}
          </div>
        )}

        {/* Reviews List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-6 h-6 text-zinc-400 animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-zinc-900 rounded-xl p-8 border border-zinc-800 text-center">
            <MessageSquare className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <h3 className="text-white font-medium mb-1">No reviews yet</h3>
            <p className="text-zinc-400 text-sm">
              {filter !== "all" || ratingFilter
                ? "No reviews match your filters"
                : "Customer reviews will appear here once they start coming in"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewCard
                key={review._id}
                review={review}
                onRespond={handleRespond}
                onDeleteResponse={handleDeleteResponse}
              />
            ))}
            {page < pages && (
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-zinc-800 text-zinc-200 border border-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
